import { Injectable, inject } from '@angular/core';
import { ApiError } from '@core/api-error';
import { Store } from '@ngrx/store';
import { AppState } from '@ra-state';
import { NavItem, UserProfile } from '@rockwell-automation-inc/common-utils';
import { filter } from 'rxjs';
import {
  acceptEULA,
  extendSession,
  getEulaConfig,
  logoutSession,
  navigateTo,
  navigateToError,
  setExtendSession,
  setHelpMenuToOpen,
  setLogoutSession,
  userLoginComplete,
} from './control-page.actions';
import {
  catalogInfo,
  getApps,
  getEnabledApps,
  isUserFound,
  mockableCatalogs,
  selectApiError,
  selectAppsMap,
  selectEulaConfig,
  selectEulaContent,
  selectEulaState,
  selectExtendSession,
  selectGetCurrentRouteState,
  selectHelpMenuToOpen,
  selectIsUserActive,
  selectLoggedInUserProfile,
  selectLogoutSession,
  selectRouteState,
  selectSessionTimedOut,
  selectVersions,
} from './control-page.selectors';

@Injectable({
  providedIn: 'root',
})
export class ControlPageFacade {
  private readonly store$ = inject<Store<AppState>>(Store);

  getApps$ = this.store$.select(getApps);
  versionInfos$ = this.store$.select(selectVersions);
  getAppsMap$ = this.store$.select(selectAppsMap);
  getEnabledApps$ = this.store$.select(getEnabledApps);
  catalogInfos$ = this.store$.pipe(catalogInfo);
  mockableCatalogs$ = this.store$.pipe(mockableCatalogs);
  sessionTimedOut$ = this.store$.select(selectSessionTimedOut);
  extendSession$ = this.store$.select(selectExtendSession);
  logoutSession$ = this.store$.select(selectLogoutSession);

  /**
   * @deprecated use @see routeStoreState$ instead
   * */
  queryParams$ = this.store$.select(selectGetCurrentRouteState);

  // REVISIT: resolve between the following two
  routeStoreState$ = this.store$.select(selectGetCurrentRouteState);
  routeState$ = this.store$.select(selectRouteState).pipe(filter((state) => state != null));

  isUserFound$ = this.store$.pipe(isUserFound);

  /// EULA should move to user state
  eulaConfig$ = this.store$.select(selectEulaConfig);
  /// why do we even need eula content in state
  eulaContent$ = this.store$.select(selectEulaContent);
  isEulaAccepted$ = this.store$.select(selectEulaState);
  isUserActive$ = this.store$.select(selectIsUserActive);
  loggedInUserProfile$ = this.store$.select(selectLoggedInUserProfile);

  apiError$ = this.store$.select(selectApiError);

  helpMenuToOpen$ = this.store$.select(selectHelpMenuToOpen);

  acceptEULA(returnTo?: string): void {
    this.store$.dispatch(acceptEULA({ value: returnTo }));
  }

  navigateTo(path: string): void {
    this.store$.dispatch(navigateTo({ path: path }));
  }

  getEulaConfig(): void {
    this.store$.dispatch(getEulaConfig());
  }

  navigateToError(apiError: ApiError): void {
    this.store$.dispatch(navigateToError({ payload: apiError }));
  }

  setUserLoggedIn(userProfile: UserProfile): void {
    this.store$.dispatch(userLoginComplete({ payload: userProfile }));
  }

  extendSession(): void {
    this.store$.dispatch(extendSession());
  }

  logoutSession(): void {
    this.store$.dispatch(logoutSession());
  }

  setExtendSession(value: boolean): void {
    this.store$.dispatch(setExtendSession({ value }));
  }

  setLogoutSession(value: boolean): void {
    this.store$.dispatch(setLogoutSession({ value }));
  }

  setHelpMenuToOpen(value: NavItem): void {
    this.store$.dispatch(setHelpMenuToOpen({ value }));
  }
}
